import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminFormSection from '../components/AdminFormSection';
import AdminSubpage from '../components/AdminSubpage';
import ProfileImageField from '../components/ProfileImageField';
import '../components/People.css';
import { customersAPI, uploadAPI } from '../services/api';
import { personName } from '../utils/person';
import { getSellerProductsPath } from '../utils/seller';

const emptyForm = {
  email: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  profile_image_url: '',
  password: '',
};

const CustomerEditPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyForm);
  const [depositBalance, setDepositBalance] = useState(0);
  const [productsCount, setProductsCount] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await customersAPI.getById(customerId);
        const customer = response.data;
        if (!cancelled && customer) {
          setFormData({
            email: customer.email || '',
            first_name: customer.first_name || '',
            last_name: customer.last_name || '',
            phone_number: customer.phone_number || '',
            profile_image_url: customer.profile_image_url || '',
            password: '',
          });
          setDepositBalance(Number(customer.auction_deposit_balance ?? 0));
          setProductsCount(Number(customer.products_count ?? 0));
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = response.data.image_url;
      setFormData((prev) => ({ ...prev, profile_image_url: imageUrl }));
      toast.success('Image uploaded');
    } catch (error) {
      let errorMessage = 'Error uploading image: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }
      toast.error(errorMessage);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const updateData = { ...formData };
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }
      if (updateData.profile_image_url === '') {
        updateData.profile_image_url = null;
      }

      await customersAPI.update(customerId, updateData);
      toast.success('Customer updated');
      navigate('/customers');
    } catch (error) {
      let errorMessage = 'Error updating customer: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      toast.error(errorMessage);
    }
  };

  return (
    <AdminSubpage title="Edit customer" backTo="/customers">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-form-page">
          <form className="admin-form-stack" onSubmit={handleSubmit}>
            <AdminFormSection title="Account" hint="Login email and optional password reset.">
              <div className="form-group">
                <label htmlFor="customer-email">Email *</label>
                <input
                  id="customer-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="customer-password">New password</label>
                <input
                  id="customer-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Leave blank to keep current"
                  minLength={6}
                />
                <small className="field-hint">Minimum 6 characters if changing.</small>
              </div>
            </AdminFormSection>

            <AdminFormSection title="Profile" hint="Buyer details shown in orders and chat.">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer-first-name">First name</label>
                  <input
                    id="customer-first-name"
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="customer-last-name">Last name</label>
                  <input
                    id="customer-last-name"
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="customer-phone">Phone number</label>
                <input
                  id="customer-phone"
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="+971501234567"
                />
              </div>
              <ProfileImageField
                value={formData.profile_image_url}
                onChange={(url) => setFormData({ ...formData, profile_image_url: url })}
                onUpload={handleImageUpload}
                uploading={uploadingImage}
                person={formData}
              />
            </AdminFormSection>

            <AdminFormSection
              title="Listings"
              hint="Products this customer is assigned as seller."
            >
              <p className="customer-listings-summary">
                <strong>{productsCount}</strong> product{productsCount === 1 ? '' : 's'} assigned
              </p>
              <button
                type="button"
                className="btn-secondary"
                onClick={() =>
                  navigate(
                    getSellerProductsPath(customerId, personName(formData)),
                  )
                }
              >
                View products
              </button>
            </AdminFormSection>

            <AdminFormSection
              title="Auction deposit"
              hint="Read-only balance from confirmed auction deposits."
            >
              <div className="form-group">
                <label htmlFor="customer-deposit">Current balance</label>
                <input
                  id="customer-deposit"
                  type="text"
                  className="admin-form-readonly"
                  value={`AED ${depositBalance.toLocaleString()}`}
                  readOnly
                />
              </div>
            </AdminFormSection>

            <div className="admin-form-footer">
              <button type="submit" className="btn btn-primary">
                Save changes
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/customers')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default CustomerEditPage;
